import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { installErrorReporting, installHttpErrorReporting } from "@/lib/errorReporter";

// Catch script errors and unhandled promise rejections so the admin Logs tab
// shows browser failures, not just server ones.
installErrorReporting();
installHttpErrorReporting();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const tree = (
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

const container = document.getElementById("root");

// Render fresh rather than hydrating the prerendered markup.
//
// react-snap snapshots the page *after* its API calls resolve, so the HTML it
// writes contains real settings, photos and merch. The browser's first render
// necessarily starts from defaults and empty queries, so the two trees differ
// and hydration fails — React error #418, "the server rendered HTML didn't
// match the client", which is what visitors were hitting on the live site.
//
// Nothing is lost by not hydrating here. The prerendered HTML still does its
// job: crawlers read it without running JavaScript, and browsers paint it
// before this bundle executes. React then replaces it with an identical tree.
// The alternative — making every data-driven component render the same on the
// server snapshot and the client's first pass — would mean giving up the
// prerendered content that makes the page worth crawling.
createRoot(container).render(tree);
